import Dexie, { type Table } from 'dexie';
import type { ItemSalidaLocal, OrdenVisitaDTO } from '@ausentia/shared';

/**
 * Base local del dispositivo (IndexedDB vía Dexie). En el prototipo web cumple
 * el rol que en producción tendría SQLCipher/Realm cifrado: caché de órdenes
 * para operar sin red, outbox de controles pendientes, adjuntos capturados
 * in-app y borradores. La especificación pide BD local cifrada con claves en
 * Keystore/Secure Enclave; eso es propio del build nativo (queda documentado).
 */

export interface OrdenCacheada {
  ordenId: string;
  datos: OrdenVisitaDTO;
  guardadaEn: string;
}

export interface AdjuntoLocal {
  id: string;
  tipo: 'CONSTANCIA_PAPEL_FOTO' | 'FACHADA_FOTO' | 'EVIDENCIA_FOTO';
  dataUrl: string;
  hashSha256: string;
  mime: string;
  capturadaEn: string;
  geo: { lat: number; lng: number; precisionMetros: number; esSimulada: boolean } | null;
  /** uploadId asignado por el servidor tras subir (si ya se subió). */
  uploadId?: string;
}

export interface Borrador {
  ordenId: string;
  datos: unknown;
  actualizadoEn: string;
}

class BaseLocal extends Dexie {
  ordenes!: Table<OrdenCacheada, string>;
  outbox!: Table<ItemSalidaLocal, number>;
  adjuntos!: Table<AdjuntoLocal, string>;
  borradores!: Table<Borrador, string>;

  constructor() {
    super('ausentia');
    this.version(1).stores({
      ordenes: 'ordenId',
      outbox: '++id, estado, ordenId, idempotencyKey',
      adjuntos: 'id, tipo',
      borradores: 'ordenId',
    });
  }
}

export const bd = new BaseLocal();

export async function guardarOrdenes(ordenes: OrdenVisitaDTO[]) {
  const ahora = new Date().toISOString();
  await bd.ordenes.bulkPut(ordenes.map((o) => ({ ordenId: o.ordenId, datos: o, guardadaEn: ahora })));
}

export async function ordenesCacheadas(): Promise<OrdenVisitaDTO[]> {
  const filas = await bd.ordenes.toArray();
  return filas
    .map((f) => f.datos)
    .sort((a, b) => a.programadaPara.localeCompare(b.programadaPara));
}

export async function guardarBorrador(ordenId: string, datos: unknown) {
  await bd.borradores.put({ ordenId, datos, actualizadoEn: new Date().toISOString() });
}

export async function leerBorrador(ordenId: string): Promise<unknown | null> {
  return (await bd.borradores.get(ordenId))?.datos ?? null;
}

export async function borrarBorrador(ordenId: string) {
  await bd.borradores.delete(ordenId);
}
